import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  where,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';
import { uploadImage } from '../../service/guardService';
import { uploadDocument } from '../../../deployment/service/documentService';

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dn9cspqmi/image/upload';
const UPLOAD_PRESET = 'ml_default';

export interface CreateGuardFormData {
  guardId: string;
  dateOfRegistration: string;
  name: string;
  fatherName: string;
  dateOfBirth: string;
  education: string;
  cnic: string;
  currentAddress: string;
  permanentAddress: string;
  contactNo1: string;
  contactNo2: string;
  salary?: string | number;
  policeDistrictCurrent: string;
  policeDistrictPermanent: string;
  sameAddress: boolean;
  language: string;
  married: boolean;
  emergencyContact: {
    name: string;
    contactNo: string;
    address: string;
    cnic: string;
  };
  services: {
    type: 'civilian' | 'serviceRetired';
    unitNo: string;
    experienceYears: string;
  };
  questions: {
    workedAsGuard: boolean;
    companyName: string;
    reasonOfLeaving: string;
    apssaTrained: boolean;
    workAnywhere: boolean;
    armyCourtCase: boolean;
  };
  references: Array<{
    name: string;
    address: string;
    cnic: string;
    contact: string;
    relation: string;
  }>;
}

export interface GuardIdGenerationResult {
  nextGuardId: string;
  prefix: string;
  nextNumber: number;
}

// Get company name
export const getCompanyName = async (): Promise<string> => {
  const uid = auth.currentUser?.uid;
  if (!uid) return '';

  try {
    const snap = await getDocs(query(collection(db, 'companies'), where('uid', '==', uid)));
    let companyName = '';
    snap.forEach((doc) => {
      companyName = doc.data().companyName || '';
    });
    return companyName;
  } catch (error) {
    console.error('Error fetching company name:', error);
    return '';
  }
};

// Generate next guard ID
export const generateNextGuardId = async (): Promise<GuardIdGenerationResult | null> => {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;

  try {
    const guardCol = collection(db, 'guards', uid, 'companyGuards');
    const snap = await getDocs(guardCol);

    if (snap.empty) {
      return { nextGuardId: 'AZ-001', prefix: 'AZ', nextNumber: 1 };
    }

    const existingIds: string[] = [];
    snap.forEach((doc) => {
      const guardData = doc.data();
      if (guardData.guardId) {
        existingIds.push(guardData.guardId);
      }
    });

    if (existingIds.length === 0) {
      return { nextGuardId: 'AZ-001', prefix: 'AZ', nextNumber: 1 };
    }

    const idPattern = /^([A-Z]+)-(\d+)$/;
    const prefixCounts: { [key: string]: number } = {};
    const allMatches: Array<{ prefix: string; number: number; digitCount: number }> = [];

    existingIds.forEach((id) => {
      const match = id.match(idPattern);
      if (match) {
        const prefix = match[1];
        prefixCounts[prefix] = (prefixCounts[prefix] || 0) + 1;
        allMatches.push({
          prefix: prefix,
          number: parseInt(match[2]),
          digitCount: match[2].length,
        });
      }
    });

    let mostCommonPrefix = 'AZ';
    let maxCount = 0;
    for (const [prefix, count] of Object.entries(prefixCounts)) {
      if (count > maxCount) {
        maxCount = count;
        mostCommonPrefix = prefix;
      }
    }

    const prefixMatches = allMatches.filter((match) => match.prefix === mostCommonPrefix);
    const numbers = prefixMatches.map((match) => match.number);
    const originalFormats = prefixMatches.map((match) => match.digitCount);

    if (numbers.length === 0) {
      return { nextGuardId: 'AZ-001', prefix: 'AZ', nextNumber: 1 };
    }

    numbers.sort((a, b) => a - b);
    let nextNumber = 1;

    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] !== nextNumber) {
        break;
      }
      nextNumber++;
    }

    if (nextNumber > numbers[numbers.length - 1]) {
      nextNumber = numbers[numbers.length - 1] + 1;
    }

    let digitFormat = 3;
    if (originalFormats.length > 0) {
      const formatCounts: { [key: number]: number } = {};
      originalFormats.forEach((format) => {
        formatCounts[format] = (formatCounts[format] || 0) + 1;
      });

      let maxCount = 0;
      let maxFormat = 3;
      Object.entries(formatCounts).forEach(([format, count]) => {
        const formatNum = parseInt(format);
        if (count > maxCount || (count === maxCount && formatNum > maxFormat)) {
          maxCount = count;
          maxFormat = formatNum;
        }
      });
      digitFormat = maxFormat;
    }

    const nextId = `${mostCommonPrefix}-${nextNumber.toString().padStart(digitFormat, '0')}`;
    return { nextGuardId: nextId, prefix: mostCommonPrefix, nextNumber };
  } catch (error) {
    console.error('Error generating guard ID:', error);
    return null;
  }
};

// Upload image to Cloudinary
export const uploadGuardImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  const response = await fetch(CLOUDINARY_URL, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (data.secure_url) {
    return data.secure_url;
  }
  throw new Error('Image upload failed');
};

// Save guard to Firebase
export const saveGuard = async (
  formData: CreateGuardFormData,
  imageFile: File | null,
  companyName: string,
  documents?: {
    apsa: { file: File | null; date: string };
    policeVerification: { file: File | null; date: string };
    others: Array<{ name: string; file: File | null; date: string }>;
  }
): Promise<string> => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  let imageUrl = 'https://res.cloudinary.com/dn9cspqmi/image/upload/v1752931322/60111_xv7kca.jpg';

  if (imageFile) {
    imageUrl = await uploadGuardImage(imageFile);
  }

  const guardCol = collection(db, 'guards', uid, 'companyGuards');
  const guardRef = doc(guardCol);
  const autoId = guardRef.id;
  const relativeLink = `/${uid}/${autoId}`;

  /* eslint-disable-next-line prefer-const */
  let documentsData: any = {
    apsa: null,
    policeVerification: null,
    others: []
  };

  if (documents) {
    // Upload APSA
    if (documents.apsa.file) {
      const result = await uploadDocument(documents.apsa.file);
      documentsData.apsa = {
        url: result.url,
        publicId: result.publicId,
        deleteToken: result.deleteToken,
        date: documents.apsa.date,
        name: 'APSA Certificate'
      };
    }

    // Upload Police Verification
    if (documents.policeVerification.file) {
      const result = await uploadDocument(documents.policeVerification.file);
      documentsData.policeVerification = {
        url: result.url,
        publicId: result.publicId,
        deleteToken: result.deleteToken,
        date: documents.policeVerification.date,
        name: 'Police Verification'
      };
    }

    // Upload Others
    if (documents.others && documents.others.length > 0) {
      for (const doc of documents.others) {
        if (doc.file) {
          const result = await uploadDocument(doc.file);
          documentsData.others.push({
            url: result.url,
            publicId: result.publicId,
            deleteToken: result.deleteToken,
            date: doc.date,
            name: doc.name
          });
        }
      }
    }
  }

  const guardData = {
    ...formData,
    image: imageUrl,
    companyName,
    link: relativeLink,
    isActive: true,
    documents: documentsData,
  };

  await setDoc(guardRef, guardData);

  return `${window.location.origin}${relativeLink}`;
};

// Format CNIC
export const formatCNIC = (v: string): string => {
  const digits = v.replace(/\D/g, '').slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5, 12)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

// Format mobile number
export const formatMobile = (v: string): string => {
  const digits = v.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}-${digits.slice(4)}`;
};

// Download Excel template
export const downloadExcelTemplate = (): void => {
  const XLSX = require('xlsx');
  const templateData = [
    {
      dateOfRegistration: '2024-01-01',
      name: 'John Doe',
      fatherName: 'Father Name',
      dateOfBirth: '1990-01-01',
      education: 'Matric',
      cnic: '12345-1234567-1',
      currentAddress: 'Current Address',
      permanentAddress: 'Permanent Address',
      contactNo1: '0300-1234567',
      contactNo2: '0301-1234567',
      policeDistrictCurrent: 'Karachi Central',
      policeDistrictPermanent: 'Karachi Central',
      sameAddress: 'FALSE',
      language: 'Urdu, English',
      married: 'TRUE',
      emergencyName: 'Emergency Contact Name',
      emergencyContactNo: '0302-1234567',
      emergencyAddress: 'Emergency Contact Address',
      emergencyCnic: '12345-1234567-2',
      serviceType: 'civilian',
      unitNo: '',
      experienceYears: '',
      workedAsGuard: 'FALSE',
      companyName: '',
      reasonOfLeaving: '',
      apssaTrained: 'TRUE',
      workAnywhere: 'TRUE',
      armyCourtCase: 'FALSE',
      ref1Name: 'Reference 1 Name',
      ref1Address: 'Reference 1 Address',
      ref1Cnic: '12345-1234567-3',
      ref1Contact: '0303-1234567',
      ref1Relation: 'Friend',
      ref2Name: 'Reference 2 Name',
      ref2Address: 'Reference 2 Address',
      ref2Cnic: '12345-1234567-4',
      ref2Contact: '0304-1234567',
      ref2Relation: 'Neighbor',
    },
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(templateData);
  const colWidths = Array(37).fill({ wch: 20 });
  ws['!cols'] = colWidths;
  XLSX.utils.book_append_sheet(wb, ws, 'Guard Template');
  const currentDate = new Date().toISOString().split('T')[0];
  const filename = `Guard_Upload_Template_${currentDate}.xlsx`;
  XLSX.writeFile(wb, filename);
};

// Handle Excel upload
export const uploadExcelFile = async (
  file: File,
  companyName: string
): Promise<number> => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const XLSX = require('xlsx');
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet);

  const guardCol = collection(db, 'guards', uid, 'companyGuards');
  const existingSnap = await getDocs(guardCol);
  const existingIds = new Set<string>();
  existingSnap.forEach((doc) => existingIds.add(doc.data().cnic?.toString().trim() || ''));

  let addedCount = 0;
  const existingGuardIds: string[] = [];
  existingSnap.forEach((doc) => {
    const guardData = doc.data();
    if (guardData.guardId) {
      existingGuardIds.push(guardData.guardId);
    }
  });

  // Generate IDs for all rows first
  const idPattern = /^([A-Z]+)-(\d+)$/;
  const prefix = 'AZ';
  const numbers: number[] = [];
  const originalFormats: number[] = [];

  existingGuardIds.forEach((id) => {
    const match = id.match(idPattern);
    if (match && match[1] === prefix) {
      const numStr = match[2];
      numbers.push(parseInt(numStr));
      originalFormats.push(numStr.length);
    }
  });

  let digitFormat = 3;
  if (originalFormats.length > 0) {
    const formatCounts: { [key: number]: number } = {};
    originalFormats.forEach((format) => {
      formatCounts[format] = (formatCounts[format] || 0) + 1;
    });
    let maxCount = 0;
    let maxFormat = 3;
    Object.entries(formatCounts).forEach(([format, count]) => {
      const formatNum = parseInt(format);
      if (count > maxCount || (count === maxCount && formatNum > maxFormat)) {
        maxCount = count;
        maxFormat = formatNum;
      }
    });
    digitFormat = maxFormat;
  }

  let currentNextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;

  for (const row of json) {
    const excelCnic = (row as any).cnic?.toString().trim();
    if (!excelCnic || existingIds.has(excelCnic)) continue;

    const newGuardId = `${prefix}-${currentNextNumber.toString().padStart(digitFormat, '0')}`;
    existingGuardIds.push(newGuardId);
    currentNextNumber++;

    const guardRef = doc(guardCol);
    const guardData = {
      guardId: newGuardId,
      dateOfRegistration: (row as any).dateOfRegistration || '',
      name: (row as any).name || '',
      fatherName: (row as any).fatherName || '',
      dateOfBirth: (row as any).dateOfBirth || '',
      education: (row as any).education || '',
      cnic: excelCnic || '',
      currentAddress: (row as any).currentAddress || '',
      permanentAddress: (row as any).permanentAddress || '',
      contactNo1: (row as any).contactNo1 || '',
      contactNo2: (row as any).contactNo2 || '',
      policeDistrictCurrent: (row as any).policeDistrictCurrent || '',
      policeDistrictPermanent: (row as any).policeDistrictPermanent || '',
      sameAddress:
        (row as any).sameAddress === true ||
        (row as any).sameAddress?.toString().toUpperCase() === 'TRUE',
      language: (row as any).language || '',
      married:
        (row as any).married === true ||
        (row as any).married?.toString().toUpperCase() === 'TRUE',
      emergencyContact: {
        name: (row as any).emergencyName || '',
        contactNo: (row as any).emergencyContactNo || '',
        address: (row as any).emergencyAddress || '',
        cnic: (row as any).emergencyCnic || '',
      },
      services: {
        type: (row as any).serviceType || 'civilian',
        unitNo: (row as any).unitNo || '',
        experienceYears: (row as any).experienceYears || '',
      },
      questions: {
        workedAsGuard:
          (row as any).workedAsGuard === true ||
          (row as any).workedAsGuard?.toString().toUpperCase() === 'TRUE',
        companyName: (row as any).companyName || '',
        reasonOfLeaving: (row as any).reasonOfLeaving || '',
        apssaTrained:
          (row as any).apssaTrained === true ||
          (row as any).apssaTrained?.toString().toUpperCase() === 'TRUE',
        workAnywhere:
          (row as any).workAnywhere === true ||
          (row as any).workAnywhere?.toString().toUpperCase() === 'TRUE',
        armyCourtCase:
          (row as any).armyCourtCase === true ||
          (row as any).armyCourtCase?.toString().toUpperCase() === 'TRUE',
      },
      references: [
        {
          name: (row as any).ref1Name || '',
          address: (row as any).ref1Address || '',
          cnic: (row as any).ref1Cnic || '',
          contact: (row as any).ref1Contact || '',
          relation: (row as any).ref1Relation || '',
        },
        {
          name: (row as any).ref2Name || '',
          address: (row as any).ref2Address || '',
          cnic: (row as any).ref2Cnic || '',
          contact: (row as any).ref2Contact || '',
          relation: (row as any).ref2Relation || '',
        },
      ],
      companyName,
      isActive: true,
    };

    await setDoc(guardRef, guardData);
    addedCount++;
  }

  return addedCount;
};

